import { get, postJson, put, deleteRequest } from './apiClient';
import { normalizeCategoryKey } from '../utils/itemCategoryKey';

/**
 * THE CATEGORY VOCABULARY — the fixed, admin-editable list a catalogue item may
 * be filed under.
 *
 * `key` is what is STORED on the row (lowercase snake_case, e.g. `deck_stores`)
 * and what every filter and order split matches on. `label` is the only thing a
 * person is ever shown. Never build a second list from these values, and never
 * print a key at a user.
 *
 * Six built-ins ship with the system — the client's own ship departments — and
 * `builtIn` rows have no `_id`, so they cannot be renamed or removed. Everything
 * an admin adds from Settings → Item Categories comes back beside them.
 */
export interface ItemCategory {
  /** Null on the six built-ins, which are constants rather than stored rows. */
  _id: string | null;
  /** The stored value. Lowercase snake_case. */
  key: string;
  /** What a person reads. */
  label: string;
  sortOrder: number;
  builtIn: boolean;
}

export async function listItemCategories(): Promise<{ success: boolean; data: { categories: ItemCategory[] } }> {
  return get<{ success: boolean; data: { categories: ItemCategory[] } }>('/item-categories');
}

export async function createItemCategory(payload: { label: string; key?: string; sortOrder?: number }) {
  return postJson<{ success: boolean; data: { category: ItemCategory } }>('/item-categories', payload);
}

export async function updateItemCategory(id: string, payload: { label?: string; sortOrder?: number }) {
  return put<{ success: boolean; data: { category: ItemCategory } }>(`/item-categories/${id}`, payload);
}

export async function deleteItemCategory(id: string) {
  return deleteRequest<{ success: boolean }>(`/item-categories/${id}`);
}

/**
 * A readable label for a value the vocabulary does not know — the categories
 * already sitting on rows from before the list existed (`Electronic`,
 * `provisions`, `safety equipment`, and the typing-test junk).
 *
 * They are shown VERBATIM, tidied only for capitalisation, and never hidden: a
 * row is not corrected by concealing what it says, and somebody has to be able
 * to see which rows still need re-filing.
 */
export function legacyCategoryLabel(value: string): string {
  const words = String(value ?? '').trim().replace(/[_-]+/g, ' ').trim();
  if (!words) return '';
  return words.replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

/**
 * The options a category picker should show: the vocabulary, plus any value
 * already stored on the rows in view so an existing row still displays its own
 * category. `unlisted` is what marks the second kind.
 *
 * Pure, so both the item form and the catalogue filter build their list the same
 * way rather than each inventing one.
 *
 * Values are compared by their normalized KEY (`normalizeCategoryKey`, the same
 * rule the server builds vocabulary keys with), never as raw strings. Raw
 * comparison listed `Electronics` and `electronics` as two options, and a row
 * stored as the label text `Engine Stores` as "(not in list)" right beside the
 * real `engine_stores` (USC-BUG-0015). A spelling of a vocabulary entry folds
 * into that entry; case/format variants of an unlisted value become one option.
 * The server's category filter matches every stored spelling of the chosen key,
 * so folding here never hides a row.
 */
export function categoryOptions(
  vocabulary: ItemCategory[],
  storedValues: Array<string | null | undefined>
): Array<{ value: string; label: string; unlisted: boolean }> {
  const options = vocabulary.map((c) => ({ value: c.key, label: c.label, unlisted: false }));
  const seen = new Set(vocabulary.map((c) => normalizeCategoryKey(c.key)));
  storedValues.forEach((raw) => {
    const value = String(raw ?? '').trim();
    const key = normalizeCategoryKey(value);
    if (!key || seen.has(key)) return;
    seen.add(key);
    options.push({ value, label: legacyCategoryLabel(value), unlisted: true });
  });
  return options;
}

/**
 * Which option a stored category selects — `Engine Stores` on a legacy row
 * selects the `engine_stores` option `categoryOptions` folded it into. A value
 * no option matches is returned as given.
 */
export function selectedCategoryOption(
  options: Array<{ value: string }>,
  raw: string | null | undefined
): string {
  const key = normalizeCategoryKey(raw);
  if (!key) return '';
  const match = options.find((o) => normalizeCategoryKey(o.value) === key);
  return match ? match.value : String(raw ?? '').trim();
}
